import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createRequestData } from '../apiHelper';
import { handleRequestGetAllClientData } from '../../Store/Internationalise/action';

/**
 * useI18nSync
 * Custom hook providing display name resolution and synchronization
 * for entities with client-specific i18n translation codes.
 */
export const useI18nSync = () => {
  const dispatch = useDispatch();
  const internationalise = useSelector((state) => state?.internationalise?.currentHashMapData);

  /**
   * Resolves display name with case-insensitive fallback.
   * Returns empty string if no translation found.
   */
  const resolveDisplayName = useCallback((code) => {
    if (!code) return '';
    return (
      internationalise?.[code] ||
      internationalise?.[code.toLowerCase()] ||
      internationalise?.[code.toUpperCase()] ||
      ''
    );
  }, [internationalise]);

  /**
   * Syncs i18n display name:
   * - If renamed, deactivates the previous i18n code (status: "INACTIVE")
   * - Adds or updates the new code (status: "ACTIVE")
   * - Refreshes the Redux i18n store
   */
  const syncI18nDisplayName = useCallback(async ({ clientId, code, originalCode, value }) => {
    const trimmedCode = (code || '').trim();
    const trimmedValue = (value || '').trim();

    if (!clientId || !trimmedCode || !trimmedValue) {
      return;
    }

    // Option B: If the code was renamed, deactivate the previous i18n entry
    if (originalCode && originalCode !== trimmedCode) {
      try {
        await createRequestData(
          {
            clientId,
            code: originalCode,
            status: 'INACTIVE'
          },
          `${process.env.REACT_APP_BASE_URL}i18`
        );
      } catch (err) {
        console.warn('Could not deactivate previous i18n translation', err);
      }
    }

    // Save or update the active translation
    try {
      await createRequestData(
        {
          clientId,
          code: trimmedCode,
          value: trimmedValue,
          status: 'ACTIVE'
        },
        `${process.env.REACT_APP_BASE_URL}i18`
      );

      // Refresh global Redux i18n store
      dispatch(handleRequestGetAllClientData(clientId, false));
    } catch (err) {
      console.error('Could not sync i18n translation', err);
    }
  }, [dispatch]);

  return {
    internationalise,
    resolveDisplayName,
    syncI18nDisplayName
  };
};

export default useI18nSync;
